import type { AxisScale } from '@visx/xychart';

import type { DomainCalculator, DomainCalculatorContext } from '../types';

/**
 * Domain calculator for band scales (used in column charts).
 * Finds categories that fall within the selected pixel range.
 */
export class BandScaleDomainCalculator implements DomainCalculator {
  calculateDomain(
    selectionStartX: number,
    selectionEndX: number,
    xScale: AxisScale,
    _context: DomainCalculatorContext,
  ): [number, number] | string[] | null {
    const bandScale = xScale as unknown as {
      domain: () => string[];
      bandwidth: () => number;
      (value: string): number | undefined;
    };

    const domain = bandScale.domain();
    const bandwidth = bandScale.bandwidth();

    // Validate domain and bandwidth
    if (!Array.isArray(domain) || domain.length === 0 || typeof bandwidth !== 'number' || bandwidth <= 0) {
      return null;
    }

    const startPixel = Math.min(selectionStartX, selectionEndX);
    const endPixel = Math.max(selectionStartX, selectionEndX);

    // Find categories that fall within the selected pixel range
    const selectedCategories: string[] = [];

    for (const category of domain) {
      const categoryPosition = bandScale(category);
      if (typeof categoryPosition === 'number' && !Number.isNaN(categoryPosition)) {
        const categoryStart = categoryPosition;
        const categoryEnd = categoryPosition + bandwidth;

        // Include category if any part of it overlaps with selection
        if (categoryEnd >= startPixel && categoryStart <= endPixel) {
          selectedCategories.push(String(category));
        }
      }
    }

    // Return selected categories (only if we found valid categories and it's a subset)
    return selectedCategories.length > 0 ? selectedCategories : null;
  }
}
